/**
 * jai1 settings init [--force] [--cloud] [--no-interactive]
 *
 * Interactive (default):  prompt user for cloud mode, validate auth
 * Non-interactive:        use flags only, no prompts
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { confirm } from '@inquirer/prompts';
import { SettingsService } from '../../services/settings.service.js';
import { ConfigService } from '../../services/config.service.js';
import { CloudTaskProvider } from '../../services/cloud-task-provider.js';
import type { Jai1Config } from '../../types/framework.types.js';
import type { SettingsInitOptions } from '../../types/settings.types.js';

/**
 * Check auth config and return status info
 */
async function checkAuthStatus(): Promise<{
    ok: boolean;
    config?: Jai1Config;
}> {
    try {
        const config = await new ConfigService().load();
        if (config?.apiUrl && config?.accessKey) {
            return { ok: true, config };
        }
        return { ok: false };
    } catch {
        return { ok: false };
    }
}

/**
 * Register project on server. Returns project_id or null on failure.
 */
async function registerProject(
    config: Jai1Config,
    projectId: string,
    repoUrl: string,
    silent: boolean,
): Promise<string | null> {
    try {
        const provider = new CloudTaskProvider(config, projectId);
        const registeredId = await provider.ensureProjectRegistered(repoUrl);
        if (!silent) {
            console.log(chalk.green('   ✓ Project registered on server'));
        }
        return registeredId;
    } catch (error) {
        if (!silent) {
            const msg = error instanceof Error ? error.message : String(error);
            console.log(chalk.yellow(`   ⚠️  Register failed: ${msg}`));
            console.log(chalk.dim('   Project sẽ được đăng ký lại khi chạy lệnh task đầu tiên.'));
        }
        return null;
    }
}

/**
 * Push existing local tasks.jsonl to cloud (if file exists and has tasks)
 */
async function syncLocalTasksToCloud(
    config: Jai1Config,
    projectId: string,
    tasksPath: string,
    silent: boolean,
): Promise<void> {
    try {
        const { existsSync, readFileSync } = await import('fs');
        if (!existsSync(tasksPath)) return;

        const content = readFileSync(tasksPath, 'utf-8').trim();
        if (!content) return;

        const { TaskSchema } = await import('../../types/task.types.js');
        const lines = content.split('\n').filter(Boolean);
        const tasks = lines.map((line) => TaskSchema.parse(JSON.parse(line)));

        if (tasks.length === 0) return;

        if (!silent) {
            console.log(chalk.dim(`   ↑ Syncing ${tasks.length} local tasks to cloud...`));
        }

        const provider = new CloudTaskProvider(config, projectId);
        await provider.push(tasks);

        if (!silent) {
            console.log(chalk.green(`   ✓ ${tasks.length} tasks synced to cloud`));
        }
    } catch {
        if (!silent) {
            console.log(chalk.dim('   ⚠️  Could not sync local tasks. Use j t sync --push later.'));
        }
    }
}

export function createSettingsInitCommand(): Command {
    return new Command('init')
        .description('Khởi tạo .jai1/settings.yaml')
        .option('-f, --force', 'Ghi đè file cũ nếu đã tồn tại')
        .option('--cloud', 'Enable cloud task sync (non-interactive)')
        .option('--no-interactive', 'Không hỏi, dùng flags/defaults')
        .option('-j, --json', 'Output JSON')
        .action(async (options: SettingsInitOptions) => {
            try {
                const service = new SettingsService();

                // Check existing file
                if (service.exists() && !options.force) {
                    throw new Error('Settings file đã tồn tại. Dùng --force để ghi đè.');
                }

                let enableCloud = options.cloud || false;
                let authResult: Awaited<ReturnType<typeof checkAuthStatus>> = { ok: false };

                // ── Interactive mode ──────────────────────────
                if (options.interactive !== false && !options.json) {
                    const repoUrl = service.resolveGitRepoUrl();

                    console.log(chalk.bold('📋 Khởi tạo Project Settings'));
                    console.log('');

                    if (repoUrl) {
                        console.log(`   ${chalk.dim('Repo:')} ${repoUrl}`);
                        console.log('');
                    }

                    enableCloud = await confirm({
                        message: 'Bật cloud task sync? (đồng bộ tasks qua server)',
                        default: true,
                    });

                    if (enableCloud) {
                        authResult = await checkAuthStatus();
                        if (!authResult.ok) {
                            console.log('');
                            console.log(chalk.yellow('⚠️  Chưa đăng nhập Jai1.'));
                            console.log(chalk.dim('   Chạy: j auth'));
                            console.log('');
                            const proceed = await confirm({
                                message: 'Vẫn bật cloud? (cần j auth trước khi dùng tasks)',
                                default: false,
                            });
                            if (!proceed) enableCloud = false;
                        } else {
                            console.log(chalk.green('   ✓ Auth OK'));
                        }
                    }
                }
                // ── Non-interactive: validate auth if --cloud ─
                else if (enableCloud) {
                    authResult = await checkAuthStatus();
                    if (!authResult.ok && !options.json) {
                        console.log(chalk.yellow('⚠️  Chưa đăng nhập. Chạy j auth trước khi dùng cloud tasks.'));
                    }
                }

                // ── Create settings ──────────────────────────
                await service.init(true); // force=true vì đã check ở trên

                if (enableCloud) {
                    await service.set('tasks.cloud', true);
                }

                // ── Register project + sync local tasks ──────
                let registeredProjectId: string | null = null;
                if (enableCloud && authResult.ok && authResult.config) {
                    const repoUrl = service.resolveGitRepoUrl();
                    const projectId = service.getProjectId();
                    if (repoUrl && projectId) {
                        registeredProjectId = await registerProject(
                            authResult.config,
                            projectId,
                            repoUrl,
                            !!options.json,
                        );
                        // Update projectId if server returned different one
                        const finalProjectId = (registeredProjectId && registeredProjectId !== projectId)
                            ? registeredProjectId
                            : projectId;
                        if (registeredProjectId && registeredProjectId !== projectId) {
                            await service.set('tasks.projectId', registeredProjectId);
                        }

                        // Auto-push local tasks to cloud (if any exist)
                        if (registeredProjectId) {
                            await syncLocalTasksToCloud(
                                authResult.config,
                                finalProjectId,
                                service.getSettingsPath().replace(/settings\.yaml$/, 'tasks.jsonl'),
                                !!options.json,
                            );
                        }
                    }
                }

                // ── Output ───────────────────────────────────
                const finalSettings = service.load();

                if (options.json) {
                    console.log(JSON.stringify({
                        success: true,
                        path: service.getSettingsPath(),
                        settings: finalSettings,
                        registered: !!registeredProjectId,
                    }, null, 2));
                    return;
                }

                console.log('');
                console.log(chalk.green('✅ Settings initialized'));
                console.log(`   ${chalk.dim('Path:')} ${service.getSettingsPath()}`);
                console.log(`   ${chalk.dim('Cloud:')} ${enableCloud ? chalk.green('enabled') : chalk.dim('disabled')}`);

                if (enableCloud) {
                    const projectId = service.getProjectId();
                    if (projectId) {
                        console.log(`   ${chalk.dim('Project ID:')} ${projectId}`);
                    }
                }
            } catch (error) {
                if (options.json) {
                    console.log(JSON.stringify({ success: false, error: error instanceof Error ? error.message : String(error) }));
                } else {
                    console.error(chalk.red(`❌ ${error instanceof Error ? error.message : String(error)}`));
                }
                process.exit(1);
            }
        });
}
